import type { Card, Transaction } from '../../types'

export type RecentTransactionItem =
  | { type: 'transaction'; transaction: Transaction; lastCreatedAt: string }
  | {
      type: 'cardGroup'
      card: Card
      total: number
      count: number
      lastFinancialDate: string
      lastCreatedAt: string
      transactions: Transaction[]
    }

function fallbackCard(id: string): Card {
  return {
    id,
    name: 'Cartão',
    last4: null,
    tone: 'dark',
    due: 15,
    close: 5,
    bill: 0,
    limit: 0,
    is_archived: false,
  }
}

function latestIsoDate(current: string, candidate: string): string {
  return candidate > current ? candidate : current
}

export function buildRecentTransactionItems(
  transactions: Transaction[],
  cards: Card[],
  limit?: number,
): RecentTransactionItem[] {
  const cardsById = new Map(cards.map(card => [card.id, card]))
  const grouped = new Map<string, Extract<RecentTransactionItem, { type: 'cardGroup' }>>()
  const items: RecentTransactionItem[] = []

  for (const transaction of transactions) {
    const lastCreatedAt = transaction.created_at || transaction.date_iso

    if (!transaction.card_id) {
      items.push({ type: 'transaction', transaction, lastCreatedAt })
      continue
    }

    const existing = grouped.get(transaction.card_id)
    if (existing) {
      existing.total += transaction.amount
      existing.count += 1
      existing.lastCreatedAt = latestIsoDate(existing.lastCreatedAt, lastCreatedAt)
      existing.lastFinancialDate = latestIsoDate(existing.lastFinancialDate, transaction.date_iso)
      existing.transactions.push(transaction)
      continue
    }

    const group: Extract<RecentTransactionItem, { type: 'cardGroup' }> = {
      type: 'cardGroup',
      card: cardsById.get(transaction.card_id) ?? fallbackCard(transaction.card_id),
      total: transaction.amount,
      count: 1,
      lastFinancialDate: transaction.date_iso,
      lastCreatedAt,
      transactions: [transaction],
    }
    grouped.set(transaction.card_id, group)
    items.push(group)
  }

  const sorted = items.sort((a, b) => {
    if (a.type === 'cardGroup' && b.type !== 'cardGroup') return -1
    if (a.type !== 'cardGroup' && b.type === 'cardGroup') return 1
    return b.lastCreatedAt.localeCompare(a.lastCreatedAt)
  })

  return limit === undefined ? sorted : sorted.slice(0, limit)
}
